import { NavLink, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  getAdminStats,
  listProducts, createProduct, deleteProduct,
  listFarmers, deleteFarmer,
  listFPOs, deleteFPO,
  listBuyers, deleteBuyer,
  listInquiries, deleteInquiry,
} from "@/lib/api";
import { Trash2, Boxes, Users, Building2, ShoppingCart, MessageSquare, LayoutDashboard, Plus, X, TreePine, PackageSearch, CalendarRange, BarChart3, Sparkles, UserPlus, FileSpreadsheet, ClipboardCheck, Users2, BookOpen } from "lucide-react";
import AdminFarmerProfiles from "@/pages/admin/AdminFarmerProfiles";
import AdminFarmerDetail from "@/pages/admin/AdminFarmerDetail";
import AdminOrchards from "@/pages/admin/AdminOrchards";
import AdminImport from "@/pages/admin/AdminImport";
import AdminFarmerInterests from "@/pages/admin/AdminFarmerInterests";
import AdminFarmerRegistrations from "@/pages/admin/AdminFarmerRegistrations";
import AdminFPOWorkflow from "@/pages/admin/AdminFPOWorkflow";
import AdminBuyerWorkflow from "@/pages/admin/AdminBuyerWorkflow";
import AdminSeasons from "@/pages/admin/AdminSeasons";
import AdminKnowledge from "@/pages/admin/AdminKnowledge";
import AdminReports from "@/pages/admin/AdminReports";

export default function Admin() {
  const { t } = useTranslation();
  const NAV = [
    { to: "/admin", label: t("admin.nav_overview"), icon: LayoutDashboard, end: true, id: "overview" },
    { to: "/admin/farmer-interests", label: t("admin_p2.interests_title"), icon: Users2, id: "farmer-interests", legacy: true },
    { to: "/admin/farmer-registrations", label: t("admin_p2.registrations_title"), icon: ClipboardCheck, id: "farmer-registrations" },
    { to: "/admin/farmer-profiles", label: "Farmer Profiles", icon: UserPlus, id: "farmer-profiles" },
    { to: "/admin/orchards", label: "Orchards", icon: TreePine, id: "orchards" },
    { to: "/admin/import", label: "CSV Import", icon: FileSpreadsheet, id: "import" },
    { to: "/admin/products", label: t("admin.nav_products"), icon: Boxes, id: "products" },
    { to: "/admin/farmers", label: "Legacy Farmers", icon: Users, id: "farmers" },
    { to: "/admin/fpos", label: t("admin.nav_fpos"), icon: Building2, id: "fpos" },
    { to: "/admin/buyers", label: t("admin.nav_buyers"), icon: ShoppingCart, id: "buyers" },
    { to: "/admin/inquiries", label: t("admin.nav_inquiries"), icon: MessageSquare, id: "inquiries" },
    { to: "/admin/seasons", label: t("seasons.title"), icon: CalendarRange, id: "seasons" },
    { to: "/admin/knowledge", label: t("knowledge.admin_title"), icon: BookOpen, id: "knowledge" },
    { to: "/admin/reports", label: t("reports.title"), icon: BarChart3, id: "reports" },
  ];

  return (
    <div data-testid="page-admin" className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="tr-eyebrow">{t("admin.eyebrow")}</div>
          <h1 className="tr-section-title text-4xl lg:text-6xl mt-4">{t("admin.title")}</h1>
        </div>
        <span className="px-3 py-1.5 rounded-full bg-[var(--tr-sage)] text-[var(--tr-deep-green)] text-xs font-bold uppercase tracking-[0.2em]">
          {t("admin.internal")}
        </span>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <nav data-testid="admin-nav" className="tr-card p-3 sticky top-24">
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.id}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                    isActive ? "bg-[var(--tr-deep-green)] text-white" : "text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                <span className="flex-1">{n.label}</span>
                {n.legacy && (
                  <span data-testid={`admin-nav-${n.id}-legacy`} className="text-[9px] font-extrabold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)] border border-[var(--tr-citrus-yellow)]/40">
                    LEGACY
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        <section className="lg:col-span-9">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="farmer-interests" element={<AdminFarmerInterests />} />
            <Route path="farmer-registrations" element={<AdminFarmerRegistrations />} />
            <Route path="farmer-profiles" element={<AdminFarmerProfiles />} />
            <Route path="farmer-profiles/:id" element={<AdminFarmerDetail />} />
            <Route path="orchards" element={<AdminOrchards />} />
            <Route path="import" element={<AdminImport />} />
            <Route path="products" element={<ProductsAdmin />} />
            <Route path="farmers" element={<FarmersAdmin />} />
            <Route path="fpos" element={<AdminFPOWorkflow />} />
            <Route path="buyers" element={<AdminBuyerWorkflow />} />
            <Route path="inquiries" element={<InquiriesAdmin />} />
            <Route path="seasons" element={<AdminSeasons />} />
            <Route path="knowledge" element={<AdminKnowledge />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </section>
      </div>
    </div>
  );
}

function Overview() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  useEffect(() => { getAdminStats().then(setStats).catch(() => setStats(null)); }, []);

  const tiles = [
    { key: "farmer_interests_new", tone: "yellow", label: "New Interests" },
    { key: "farmer_reg_submitted", tone: "orange", label: "Pending Review" },
    { key: "farmer_reg_approved", tone: "green", label: "Approved Farmers" },
    { key: "orchards", tone: "green", label: "Orchards" },
    { key: "products_live", tone: "yellow", label: t("admin.stat_products_live") },
    { key: "inquiries_new", tone: "orange", label: t("admin.stat_inquiries_new") },
  ];

  const modules = [
    { icon: PackageSearch, k: "procurement" },
    { icon: CalendarRange, k: "harvest" },
    { icon: BarChart3, k: "reports" },
  ];

  return (
    <div data-testid="admin-overview">
      <h2 className="tr-section-title text-3xl">{t("admin.overview_title")}</h2>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {tiles.map((tt) => (
          <div key={tt.key} className="tr-card p-6" data-testid={`stat-${tt.key}`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-muted)] font-bold">
              {tt.label}
            </div>
            <div className={`mt-3 font-display font-extrabold text-4xl ${
              tt.tone === "yellow" ? "text-[var(--tr-citrus-yellow)]" :
              tt.tone === "orange" ? "text-[var(--tr-zest)]" :
              "text-[var(--tr-deep-green)]"
            }`}>
              {stats ? stats[tt.key] : "—"}
            </div>
          </div>
        ))}
      </div>

      {/* Future modules — placeholders only; deferred to next sprint */}
      <div className="mt-14" data-testid="admin-future-modules">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
          <h3 className="font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t("admin.modules_title")}</h3>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-zest)] font-bold">{t("common.coming_soon")}</span>
        </div>
        <p className="text-[var(--tr-muted)] max-w-3xl leading-relaxed">{t("admin.modules_body")}</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => (
            <div
              key={m.k}
              data-testid={`admin-module-${m.k}`}
              className="relative rounded-2xl border border-dashed border-[rgba(6,78,59,0.25)] bg-white/60 p-6 flex flex-col justify-between min-h-[180px] opacity-90 hover:opacity-100 transition-opacity"
            >
              <div className="flex items-start justify-between">
                <m.icon className="w-6 h-6 text-[var(--tr-deep-green)]" />
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)] text-[10px] font-bold uppercase tracking-[0.18em]">
                  <Sparkles className="w-3 h-3" /> {t("common.coming_soon")}
                </span>
              </div>
              <div className="mt-4">
                <div className="font-display font-extrabold text-lg text-[var(--tr-deep-green)]">{t(`admin.cs_${m.k}`)}</div>
                <p className="mt-2 text-sm text-[var(--tr-muted)] leading-relaxed">{t(`admin.cs_${m.k}_d`)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DataTable({ testid, rows, columns, onDelete }) {
  const { t } = useTranslation();
  if (!rows) return <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>;
  if (rows.length === 0) return <div data-testid={`${testid}-empty`} className="tr-card p-10 text-center text-[var(--tr-muted)]">{t("admin.empty_state")}</div>;
  return (
    <div className="tr-card overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--tr-sage)]/50">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{c.label}</th>
              ))}
              <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`${testid}-row-${r.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 align-top text-[var(--tr-deep-green)]">
                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <button data-testid={`${testid}-delete-${r.id}`} onClick={() => onDelete(r.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] hover:text-red-700 text-sm font-semibold">
                    <Trash2 className="w-4 h-4" /> {t("common.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductsAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", category: "fruits", tagline: "", short_description: "", image_url: "", is_live: true });

  const load = () => listProducts().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    await deleteProduct(id);
    toast.success(t("toast.deleted"));
    load();
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createProduct(form);
      toast.success(t("toast.product_created"));
      setShowForm(false);
      setForm({ slug: "", name: "", category: "fruits", tagline: "", short_description: "", image_url: "", is_live: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.failed"));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="tr-section-title text-3xl">{t("admin.products_title")}</h2>
        <button data-testid="admin-add-product" onClick={() => setShowForm((s) => !s)} className="tr-pill-btn tr-btn-primary">
          {showForm ? <><X className="w-4 h-4" /> {t("common.cancel")}</> : <><Plus className="w-4 h-4" /> {t("admin.products_add")}</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="admin-product-form" className="tr-card p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label={`${t("admin.p_slug")}*`} value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="pf-slug" />
          <Input label={`${t("admin.p_name")}*`} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="pf-name" />
          <label className="block">
            <span className="tr-label">{t("admin.p_category")}</span>
            <select data-testid="pf-category" className="tr-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {["fruits", "vegetables", "grains", "millets", "pulses", "spices", "other"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <Input label={t("admin.p_tagline")} value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="pf-tagline" />
          <Input label={t("admin.p_image_url")} value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="pf-image" className="md:col-span-2" />
          <label className="block md:col-span-2">
            <span className="tr-label">{t("admin.p_short_desc")}</span>
            <textarea data-testid="pf-desc" className="tr-input tr-textarea" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
          </label>
          <div className="md:col-span-2">
            <button data-testid="admin-product-submit" type="submit" className="tr-pill-btn tr-btn-zest">{t("admin.p_create")}</button>
          </div>
        </form>
      )}

      <DataTable
        testid="admin-products-table"
        rows={items}
        columns={[
          { key: "name", label: t("admin.p_name"), render: (r) => <span className="font-semibold">{r.name}</span> },
          { key: "slug", label: t("admin.p_slug") },
          { key: "category", label: t("admin.p_category") },
          { key: "is_live", label: t("admin.p_live"), render: (r) => r.is_live
            ? <span className="px-2 py-0.5 rounded-full bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)] text-[10px] font-bold uppercase">{t("admin.p_live")}</span>
            : <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold uppercase">{t("admin.p_draft")}</span>
          },
        ]}
        onDelete={onDelete}
      />
    </div>
  );
}

function FarmersAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const load = () => listFarmers().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const onDelete = async (id) => { if (!window.confirm(t("admin.confirm_delete"))) return; await deleteFarmer(id); toast.success(t("toast.deleted")); load(); };
  return (
    <div>
      <h2 className="tr-section-title text-3xl mb-6">{t("admin.farmers_title")}</h2>
      <DataTable
        testid="admin-farmers-table"
        rows={items}
        columns={[
          { key: "full_name", label: t("admin.col_name") },
          { key: "phone", label: t("admin.col_phone") },
          { key: "commodity", label: t("admin.col_commodity") },
          { key: "location", label: t("admin.col_location"), render: (r) => `${r.village}, ${r.district}, ${r.state}` },
          { key: "acres", label: t("admin.col_acres") },
        ]}
        onDelete={onDelete}
      />
    </div>
  );
}

function FPOsAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const load = () => listFPOs().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const onDelete = async (id) => { if (!window.confirm(t("admin.confirm_delete"))) return; await deleteFPO(id); toast.success(t("toast.deleted")); load(); };
  return (
    <div>
      <h2 className="tr-section-title text-3xl mb-6">{t("admin.fpos_title")}</h2>
      <DataTable
        testid="admin-fpos-table"
        rows={items}
        columns={[
          { key: "name", label: t("admin.col_fpo") },
          { key: "contact_person", label: t("admin.col_contact") },
          { key: "phone", label: t("admin.col_phone") },
          { key: "location", label: t("admin.col_location"), render: (r) => `${r.district}, ${r.state}` },
          { key: "member_count", label: t("admin.col_members") },
        ]}
        onDelete={onDelete}
      />
    </div>
  );
}

function BuyersAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const load = () => listBuyers().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const onDelete = async (id) => { if (!window.confirm(t("admin.confirm_delete"))) return; await deleteBuyer(id); toast.success(t("toast.deleted")); load(); };
  return (
    <div>
      <h2 className="tr-section-title text-3xl mb-6">{t("admin.buyers_title")}</h2>
      <DataTable
        testid="admin-buyers-table"
        rows={items}
        columns={[
          { key: "company_name", label: t("admin.col_company") },
          { key: "contact_person", label: t("admin.col_contact") },
          { key: "buyer_type", label: t("admin.col_type") },
          { key: "email", label: t("admin.col_email") },
          { key: "location", label: t("admin.col_location"), render: (r) => `${r.city}, ${r.country}` },
        ]}
        onDelete={onDelete}
      />
    </div>
  );
}

function InquiriesAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const load = () => listInquiries().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const onDelete = async (id) => { if (!window.confirm(t("admin.confirm_delete"))) return; await deleteInquiry(id); toast.success(t("toast.deleted")); load(); };
  return (
    <div>
      <h2 className="tr-section-title text-3xl mb-6">{t("admin.inquiries_title")}</h2>
      <DataTable
        testid="admin-inquiries-table"
        rows={items}
        columns={[
          { key: "name", label: t("admin.col_name") },
          { key: "email", label: t("admin.col_email") },
          { key: "source", label: t("admin.col_source") },
          { key: "subject", label: t("admin.col_subject") },
          { key: "message", label: t("admin.col_message"), render: (r) => <span className="line-clamp-2 max-w-md block">{r.message}</span> },
        ]}
        onDelete={onDelete}
      />
    </div>
  );
}

function Input({ label, value, onChange, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="tr-label">{label}</span>
      <input data-testid={testid} className="tr-input" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
